import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CaseEvent, CaseLinkKind, CaseOutcome, CaseStatus, ComplianceCase, RiskSignal } from '@regigov/contracts';
import { casesApi, riskApi } from '../api/compliance';
import { useAuth } from '../auth/AuthContext';
import { DefinitionList, Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';
import { InspectionOutcomes, InspectionPanel } from './InspectionPanel';
import { AuditPanel, ParticipantFollowUps } from './AuditPanel';
import { EnforcementPanel } from './EnforcementPanel';
import { REGISTER_PATH, SOURCE_LABEL, SOURCE_PATH } from './RiskPage';

type Mode = 'cer' | 'participant';
type Paths = { mode: Mode; basePath: string };

const STATUSES: CaseStatus[] = ['open', 'assessing', 'acting', 'closed'];
const OUTCOMES: Record<CaseOutcome, string> = { no_further_action: 'No further action', remediated: 'Remediated', enforcement: 'Referred for enforcement' };
const LINK_LABEL: Record<CaseLinkKind, string> = { ...SOURCE_LABEL, inspection: 'Inspection', audit: 'Audit', enforcement_action: 'Enforcement action' };
const NEXT: Record<CaseStatus, CaseStatus | null> = { open: 'assessing', assessing: 'acting', acting: null, closed: null };

function Due({ dueOn, overdue }: { dueOn: string; overdue: boolean }) {
  return (
    <>
      {dueOn} {overdue && <StatusBadge status="overdue" />}
    </>
  );
}

/** Case register: every case for CER; for a participant, only cases it is the subject of (RLS). */
export function CasesPage({ mode, basePath }: Paths) {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as CaseStatus | '';
  const overdue = params.get('overdue') === 'true';
  const query = { status: status || undefined, overdue: overdue ? ('true' as const) : undefined };
  const list = useQuery({ queryKey: ['cases', query], queryFn: () => casesApi.list(query) });
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  return (
    <>
      <div className="row">
        <h1>{mode === 'cer' ? 'Compliance cases' : 'Compliance'}</h1>
        <span className="grow" />
        {mode === 'cer' && <Link to="/cer/compliance/risk">Open a case from the risk view →</Link>}
      </div>
      <p className="muted">
        {mode === 'cer'
          ? `Cases move open → assessing → acting → closed. Each is due ${list.data?.slaBusinessDays ?? '…'} business days after it is opened, and every step is appended to its timeline.`
          : 'Compliance cases CER has opened about your organisation. Acknowledge each case and attach evidence of remediation; CER records its assessment and outcome on the same timeline.'}
      </p>
      <div className="row">
        <select value={status} onChange={(e) => set('status', e.target.value)} aria-label="Status filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label>
          <input type="checkbox" checked={overdue} onChange={(e) => set('overdue', e.target.checked ? 'true' : '')} /> Overdue only
        </label>
      </div>
      <ProblemAlert error={list.error} />
      {list.data &&
        (list.data.items.length === 0 ? (
          <p>No cases.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Title</th>
                {mode === 'cer' && <th>Subject</th>}
                <th>Status</th>
                <th>Due</th>
                <th>Acknowledged</th>
                <th>Opened</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`${basePath}/${c.id}`}>
                      <code>{c.id}</code>
                    </Link>
                  </td>
                  <td>{c.title}</td>
                  {mode === 'cer' && <td>{c.subjectParticipantName}</td>}
                  <td>
                    <StatusBadge status={c.status} />
                  </td>
                  <td>
                    <Due dueOn={c.dueOn} overdue={c.overdue} />
                  </td>
                  <td>{c.acknowledgedAt ? formatDateTime(c.acknowledgedAt) : <span className="muted">Not yet</span>}</td>
                  <td>{c.openedOn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      {mode === 'participant' && <InspectionOutcomes basePath={basePath} />}
      {mode === 'participant' && <ParticipantFollowUps basePath={basePath} />}
    </>
  );
}

/** Opens a case from a participant's risk row; the selected live signals are re-read and snapshotted by the API. */
export function NewCasePage() {
  const [params] = useSearchParams();
  const participantId = params.get('participant') ?? '';
  const navigate = useNavigate();
  const risk = useQuery({ queryKey: ['compliance', 'risk'], queryFn: riskApi.view, staleTime: 0, gcTime: 0 });
  const row = risk.data?.rows.find((r) => r.participantId === participantId);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const { user } = useAuth();
  const owners = useQuery({ queryKey: ['cases', 'owners'], queryFn: casesApi.owners });
  const units = useQuery({ queryKey: ['cases', 'subject-units', participantId], queryFn: () => casesApi.subjectUnits(participantId), enabled: Boolean(participantId) });
  const [ownerUserId, setOwnerUserId] = useState('');
  const owner = ownerUserId || user?.id || '';
  const [selectedUnitId, setUnitId] = useState('');
  const unitId = units.data?.items.some((u) => u.id === selectedUnitId) ? selectedUnitId : '';
  const key = (s: RiskSignal) => `${s.source}:${s.sourceId}`;
  const open = useMutation({
    mutationFn: () =>
      casesApi.open({
        subjectParticipantId: participantId,
        subjectUnitId: unitId || undefined,
        ownerUserId: owner,
        title: title || `Compliance review: ${row?.participantName ?? participantId}`,
        summary,
        signals: (row?.signals ?? []).filter((s) => !excluded.has(key(s))).map((s) => ({ source: s.source, sourceId: s.sourceId })),
      }),
    onSuccess: (c) => navigate(`/cer/compliance/cases/${c.id}`),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    open.mutate();
  };
  return (
    <>
      <p>
        <Link to="/cer/compliance/risk">← Risk view</Link>
      </p>
      <h1>Open a case</h1>
      <ProblemAlert error={risk.error} />
      {risk.data && !row && <p className="notice">No risk row for {participantId || 'that participant'}. Open cases from a participant's row in the risk view.</p>}
      {row && (
        <form className="form card" onSubmit={submit}>
          <DefinitionList items={[['Subject', <Link to={`/cer/participants/${participantId}`}>{row.participantName ?? participantId}</Link>], ['Risk score', String(row.score)]]} />
          <Field label="Title">
            <input value={title} placeholder={`Compliance review: ${row.participantName ?? participantId}`} onChange={(e) => setTitle(e.target.value)} disabled={open.isPending} />
          </Field>
          <Field label="Owner" hint="The CER officer responsible for the case. It can be reassigned later.">
            <select value={owner} onChange={(e) => setOwnerUserId(e.target.value)} required disabled={open.isPending}>
              {!owners.data?.items.some((o) => o.id === owner) && <option value="">Choose…</option>}
              {owners.data?.items.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName} ({o.username})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Unit (optional)" hint={`A unit ${row.participantName ?? participantId} owns, manufactures or installed, if the case is about one device.`}>
            <select value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={open.isPending}>
              <option value="">Whole participant</option>
              {units.data?.items.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.serial} · {u.modelNumber} · {u.relations.join(', ')}
                </option>
              ))}
            </select>
          </Field>
          <ProblemAlert error={owners.error ?? units.error} />
          <Field label="Summary" hint="Why the case is opened; shared with the participant.">
            <textarea rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} required minLength={10} disabled={open.isPending} />
          </Field>
          <h3>Signals carried into the case</h3>
          <table className="table">
            <tbody>
              {row.signals.map((s) => (
                <tr key={key(s)}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Link ${s.title}`}
                      checked={!excluded.has(key(s))}
                      onChange={(e) => {
                        const next = new Set(excluded);
                        if (e.target.checked) next.delete(key(s));
                        else next.add(key(s));
                        setExcluded(next);
                      }}
                    />
                  </td>
                  <td>{SOURCE_LABEL[s.source]}</td>
                  <td>
                    {s.title}
                    <div className="muted">{s.detail}</div>
                  </td>
                  <td>{s.weight}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ProblemAlert error={open.error} />
          <div>
            <button type="submit" disabled={open.isPending || summary.trim().length < 10 || !owners.data?.items.some((o) => o.id === owner)}>
              Open case
            </button>
          </div>
        </form>
      )}
    </>
  );
}

function eventLabel(e: CaseEvent): string {
  if (e.kind === 'status') return `${e.fromStatus} → ${e.toStatus}${e.outcome ? ` (${OUTCOMES[e.outcome]})` : ''}`;
  if (e.kind === 'correction') return `correction of #${e.correctsEventId}`;
  if (e.kind === 'inspection') return `inspection ${e.inspectionId ?? ''}`.trim();
  if (e.kind === 'audit') return `audit ${e.auditId ?? ''}${e.auditFindingId ? ` follow-up` : ''}`.trim();
  if (e.kind === 'enforcement') return `enforcement ${e.enforcementActionId ?? ''}`.trim();
  if (e.kind === 'owner') return `owner: ${e.fromOwnerName ?? '?'} → ${e.toOwnerName ?? '?'}`;
  return e.kind;
}

export function CaseDetailPage({ mode, basePath }: Paths) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['case', id], queryFn: () => casesApi.get(id) });
  const onSaved = (c: ComplianceCase) => {
    qc.setQueryData(['case', id], c);
    void qc.invalidateQueries({ queryKey: ['cases'] });
  };
  const c = query.data;
  return (
    <>
      <p>
        <Link to={basePath}>← {mode === 'cer' ? 'Compliance cases' : 'Compliance'}</Link>
        {mode === 'cer' && (
          <>
            {' · '}
            <Link to={`/cer/audit?entityId=${encodeURIComponent(id)}&related=true`}>Audit trail</Link>
          </>
        )}
      </p>
      <ProblemAlert error={query.error} />
      {c && (
        <>
          <div className="row">
            <h1>{c.title}</h1>
            <StatusBadge status={c.status} />
            {c.overdue && <StatusBadge status="overdue" />}
          </div>
          <section className="card">
            <DefinitionList
              items={[
                ['Case ID', <code>{c.id}</code>],
                ['Subject', mode === 'cer' ? <Link to={`/cer/participants/${c.subjectParticipantId}`}>{c.subjectParticipantName}</Link> : c.subjectParticipantName],
                ['Unit', c.subjectUnitId ? (c.subjectUnitSerial ?? c.subjectUnitId) : null],
                ['Owner', mode === 'cer' ? (c.ownerName ?? c.ownerUserId) : 'CER compliance officer'],
                ['Opened', c.openedOn],
                ['Due', <Due dueOn={c.dueOn} overdue={c.overdue} />],
                ['SLA', `${c.slaBusinessDays} business days`],
                ['Acknowledged', c.acknowledgedAt ? formatDateTime(c.acknowledgedAt) : 'Not yet'],
                ['Outcome', c.outcome ? `${OUTCOMES[c.outcome]}: ${c.outcomeReason}` : null],
                ['Closed', c.closedAt ? formatDateTime(c.closedAt) : null],
              ]}
            />
            <h3>Summary</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{c.summary}</p>
          </section>

          <h2>Linked signals</h2>
          {c.links.length === 0 ? (
            <p className="muted">No linked signals.</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Weight</th>
                  <th>Signal (as linked)</th>
                  <th>Observed</th>
                  <th>Source record</th>
                  <th>Register record</th>
                </tr>
              </thead>
              <tbody>
                {c.links.map((l) => {
                  const path = mode === 'cer' && l.sourceRecord.kind !== 'inspection' && l.sourceRecord.kind !== 'audit' && l.sourceRecord.kind !== 'enforcement_action' ? SOURCE_PATH[l.sourceRecord.kind]?.(l.sourceRecord.id) : undefined;
                  return (
                    <tr key={l.id}>
                      <td>{LINK_LABEL[l.kind]}</td>
                      <td>{l.weight ?? '—'}</td>
                      <td>
                        {l.title}
                        <div className="muted">{l.detail}</div>
                      </td>
                      <td>{formatDateTime(l.observedAt)}</td>
                      <td>{path ? <Link to={path}>{l.sourceRecord.label}</Link> : <code>{l.sourceRecord.label}</code>}</td>
                      <td>
                        {l.registerRecord ? (
                          <Link to={`${mode === 'cer' ? '/cer/register' : '/participant/devices'}/${REGISTER_PATH[l.registerRecord.kind]}/${l.registerRecord.id}`}>{l.registerRecord.label}</Link>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <InspectionPanel c={c} mode={mode} />

          <AuditPanel c={c} mode={mode} />

          <EnforcementPanel c={c} mode={mode} />

          <h2>Case timeline</h2>
          <p className="muted">Append-only: entries are never edited. A correction is a new entry that points at the one it corrects.</p>
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>When</th>
                <th>Event</th>
                <th>By</th>
                <th>Note</th>
                {mode === 'cer' && <th>Visible to participant</th>}
              </tr>
            </thead>
            <tbody>
              {c.events.map((e) => (
                <tr key={e.id}>
                  <td>{e.id}</td>
                  <td>{formatDateTime(e.createdAt)}</td>
                  <td>{eventLabel(e)}</td>
                  <td>{e.actorParticipantName ?? (mode === 'cer' ? (e.actorName ?? '—') : 'CER')}</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>
                    {e.note}
                    {e.evidenceRef && (
                      <div>
                        Evidence: <code>{e.evidenceRef}</code>
                      </div>
                    )}
                    {e.reason && <div className="muted">Reason: {e.reason}</div>}
                    {e.correctedBy.length > 0 && <div className="muted">Corrected by #{e.correctedBy.join(', #')}</div>}
                  </td>
                  {mode === 'cer' && <td>{e.shared ? 'Yes' : 'No'}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          {c.status !== 'closed' && (mode === 'cer' ? <CerActions c={c} onSaved={onSaved} /> : <ParticipantActions c={c} onSaved={onSaved} />)}
        </>
      )}
    </>
  );
}

type ActionProps = { c: ComplianceCase; onSaved: (c: ComplianceCase) => void };

function CerActions({ c, onSaved }: ActionProps) {
  const next = NEXT[c.status];
  const [statusNote, setStatusNote] = useState('');
  const [outcome, setOutcome] = useState<CaseOutcome>('remediated');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [shared, setShared] = useState(false);
  const move = useMutation({ mutationFn: () => casesApi.status(c.id, { status: next as 'assessing' | 'acting', note: statusNote }), onSuccess: (r) => (setStatusNote(''), onSaved(r)) });
  const close = useMutation({ mutationFn: () => casesApi.close(c.id, { outcome, reason }), onSuccess: onSaved });
  const addNote = useMutation({ mutationFn: () => casesApi.note(c.id, { note, shared }), onSuccess: (r) => (setNote(''), onSaved(r)) });
  const owners = useQuery({ queryKey: ['cases', 'owners'], queryFn: casesApi.owners });
  const [newOwner, setNewOwner] = useState('');
  const [ownerReason, setOwnerReason] = useState('');
  const reassign = useMutation({ mutationFn: () => casesApi.reassign(c.id, { ownerUserId: newOwner, reason: ownerReason }), onSuccess: (r) => (setNewOwner(''), setOwnerReason(''), onSaved(r)) });
  return (
    <>
      {next && (
        <form className="form card" onSubmit={(e) => (e.preventDefault(), move.mutate())}>
          <h2>Move to {next}</h2>
          <textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} required placeholder="What was done or decided" disabled={move.isPending} />
          <ProblemAlert error={move.error} />
          <div>
            <button type="submit" disabled={move.isPending || statusNote.trim().length < 3}>
              Move to {next}
            </button>
          </div>
        </form>
      )}
      {c.status !== 'open' && (
        <form className="form card" onSubmit={(e) => (e.preventDefault(), close.mutate())}>
          <h2>Close case</h2>
          <Field label="Outcome">
            <select value={outcome} onChange={(e) => setOutcome(e.target.value as CaseOutcome)} disabled={close.isPending}>
              {Object.entries(OUTCOMES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason">
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} required disabled={close.isPending} />
          </Field>
          <ProblemAlert error={close.error} />
          <div>
            <button type="submit" disabled={close.isPending || reason.trim().length < 3}>
              Close case
            </button>
          </div>
        </form>
      )}
      <form className="form card" onSubmit={(e) => (e.preventDefault(), reassign.mutate())}>
        <h2>Reassign owner</h2>
        <Field label="New owner">
          <select value={newOwner} onChange={(e) => setNewOwner(e.target.value)} required disabled={reassign.isPending}>
            <option value="">Choose…</option>
            {owners.data?.items
              .filter((o) => o.id !== c.ownerUserId)
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.displayName} ({o.username})
                </option>
              ))}
          </select>
        </Field>
        <Field label="Reason">
          <input value={ownerReason} onChange={(e) => setOwnerReason(e.target.value)} required disabled={reassign.isPending} />
        </Field>
        <p className="muted">Recorded on the timeline (CER-only).</p>
        <ProblemAlert error={owners.error ?? reassign.error} />
        <div>
          <button type="submit" disabled={reassign.isPending || !newOwner || ownerReason.trim().length < 3}>
            Reassign
          </button>
        </div>
      </form>
      <form className="form card" onSubmit={(e) => (e.preventDefault(), addNote.mutate())}>
        <h2>Add a note</h2>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required disabled={addNote.isPending} />
        <label>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Visible to {c.subjectParticipantName}
        </label>
        <ProblemAlert error={addNote.error} />
        <div>
          <button type="submit" disabled={addNote.isPending || note.trim().length === 0}>
            Add note
          </button>
        </div>
      </form>
      <CorrectionForm c={c} onSaved={onSaved} own={(e) => e.actorParticipantId === null} />
    </>
  );
}

function ParticipantActions({ c, onSaved }: ActionProps) {
  const [ackNote, setAckNote] = useState('');
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const acknowledge = useMutation({ mutationFn: () => casesApi.acknowledge(c.id, { note: ackNote || undefined }), onSuccess: onSaved });
  const evidence = useMutation({ mutationFn: () => casesApi.evidence(c.id, { note, evidenceRef }), onSuccess: (r) => (setNote(''), setEvidenceRef(''), onSaved(r)) });
  return (
    <>
      {!c.acknowledgedAt && (
        <form className="form card" onSubmit={(e) => (e.preventDefault(), acknowledge.mutate())}>
          <h2>Acknowledge</h2>
          <p className="muted">Confirms your organisation has received this case. CER sees who acknowledged it and when.</p>
          <textarea rows={2} value={ackNote} onChange={(e) => setAckNote(e.target.value)} placeholder="Optional note" disabled={acknowledge.isPending} />
          <ProblemAlert error={acknowledge.error} />
          <div>
            <button type="submit" disabled={acknowledge.isPending}>
              Acknowledge
            </button>
          </div>
        </form>
      )}
      <form className="form card" onSubmit={(e) => (e.preventDefault(), evidence.mutate())}>
        <h2>Attach remediation evidence</h2>
        <Field label="What was remediated">
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required disabled={evidence.isPending} />
        </Field>
        <Field label="Evidence reference" hint="Document ID, ticket number or URL of the evidence held by your organisation.">
          <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} required disabled={evidence.isPending} />
        </Field>
        <ProblemAlert error={evidence.error} />
        <div>
          <button type="submit" disabled={evidence.isPending || note.trim().length < 3 || evidenceRef.trim().length === 0}>
            Attach evidence
          </button>
        </div>
      </form>
      <CorrectionForm c={c} onSaved={onSaved} own={(e) => e.actorParticipantId === c.subjectParticipantId} />
    </>
  );
}

function CorrectionForm({ c, onSaved, own }: ActionProps & { own: (e: CaseEvent) => boolean }) {
  const targets = c.events.filter((e) => (e.kind === 'note' || e.kind === 'evidence') && own(e));
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [reason, setReason] = useState('');
  const picked = targets.find((e) => String(e.id) === target);
  const correct = useMutation({
    mutationFn: () => casesApi.correct(c.id, { correctsEventId: Number(target), note, evidenceRef: picked?.kind === 'evidence' ? evidenceRef : undefined, reason }),
    onSuccess: (r) => (setTarget(''), setNote(''), setEvidenceRef(''), setReason(''), onSaved(r)),
  });
  if (targets.length === 0) return null;
  return (
    <form className="form card" onSubmit={(e) => (e.preventDefault(), correct.mutate())}>
      <h2>Correct an entry</h2>
      <Field label="Entry">
        <select value={target} onChange={(e) => setTarget(e.target.value)} required disabled={correct.isPending}>
          <option value="">Choose…</option>
          {targets.map((e) => (
            <option key={e.id} value={e.id}>
              #{e.id} {e.kind}: {(e.note ?? '').slice(0, 60)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Corrected text">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required disabled={correct.isPending} />
      </Field>
      {picked?.kind === 'evidence' && (
        <Field label="Corrected evidence reference">
          <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} required disabled={correct.isPending} />
        </Field>
      )}
      <Field label="Reason for correction">
        <input value={reason} onChange={(e) => setReason(e.target.value)} required disabled={correct.isPending} />
      </Field>
      <ProblemAlert error={correct.error} />
      <div>
        <button type="submit" disabled={correct.isPending || !picked || reason.trim().length < 3}>
          Append correction
        </button>
      </div>
    </form>
  );
}
